import {
  useArchPrice,
  useGetPositionLifetimeInDays,
  useLevMultiplier,
  useLeverageAvailable,
  useLvUSDToArchRatio,
  useOriginationFee,
  useOusdApy,
  usePerformanceFee,
} from '@components/store/getters';
import {
  Wrapper,
  Container,
  Title,
  Row,
  Label,
  Value,
  Highlighted,
  PercentageBarWrap,
  PercentageBar,
  PercentageValue,
} from './Summary.styles';
import { useMemo } from 'react';
import { roundNumber } from '../../../utils/roundNumber';
import CountUp from 'react-countup';
import Tooltip from '@components/common/Tooltip';
import NetApyCalculationTooltip from '../NetApyCalculationTooltip';

interface SummaryProps {
  estimatedPosition: number;
  collateral?: number;
  tokenFeeInArch?: number;
  tokenFeeInUsd?: number;
  useArchFromWallet: boolean;
}

export const Summary = ({
  estimatedPosition,
  collateral,
  tokenFeeInArch,
  tokenFeeInUsd,
  useArchFromWallet,
}: SummaryProps) => {
  const leverageAvailable = useLeverageAvailable();
  const archToLevRatio = Number(useLvUSDToArchRatio());
  const archPrice = Number(useArchPrice());
  const performanceFee = Number(usePerformanceFee()) * 100;
  const ousdAPY = useOusdApy();
  const leverageMultiplier = useLevMultiplier();
  const positionLifetimeInDays = useGetPositionLifetimeInDays();
  const originationFee = useOriginationFee();

  // const archPriceFormatted = useMemo(() => {
  //   if (archPrice) return roundNumber(archPrice, 2);
  // }, [archPrice]);

  const leverageMultiplierFormatted = useMemo(() => {
    if (leverageMultiplier) return roundNumber(leverageMultiplier + 1, 2);
  }, [leverageMultiplier]);

  const originationFeePercentage = useMemo(() => {
    if (originationFee) return Number(originationFee) * 100;
  }, [originationFee]);

  const originationFeeInUsdFormatted = useMemo(() => {
    if (originationFee && estimatedPosition) return roundNumber(Number(originationFee) * estimatedPosition, 2);
  }, [originationFee, estimatedPosition]);

  const leveragedApy = useMemo(() => {
    if (ousdAPY && leverageMultiplier) {
      return ousdAPY * (leverageMultiplier + 1);
    }
  }, [ousdAPY, leverageMultiplier]);

  const multiplierBasedOnPerformanceFee = useMemo(() => {
    if (performanceFee) {
      return 1 - performanceFee / 100;
    }
  }, [performanceFee]);

  const finalOusdApy = useMemo(() => {
    if (leveragedApy && multiplierBasedOnPerformanceFee) {
      return roundNumber(leveragedApy * multiplierBasedOnPerformanceFee, 2);
    }
  }, [leveragedApy, multiplierBasedOnPerformanceFee]);

  const archTokenContent = useMemo(() => {
    if (!tokenFeeInArch || !tokenFeeInUsd) return '--';
    if (useArchFromWallet)
      return (
        <>
          <CountUp end={tokenFeeInArch} duration={1} decimals={2} /> ARCH
        </>
      );
    return (
      <>
        <CountUp end={tokenFeeInArch} duration={1} decimals={2} /> ARCH ≈ $<CountUp end={tokenFeeInUsd} decimals={2} />
      </>
    );
  }, [tokenFeeInArch, tokenFeeInUsd, useArchFromWallet]);

  const originationFeeContent = useMemo(() => {
    if (!originationFeePercentage || !originationFeeInUsdFormatted) return '--';
    return (
      <>
        <CountUp end={originationFeePercentage} duration={1} decimals={2} />% ≈ $
        <CountUp end={originationFeeInUsdFormatted} decimals={2} />
      </>
    );
  }, [originationFeePercentage, originationFeeInUsdFormatted]);

  return (
    <Wrapper>
      <Container>
        {/* <Title>
          Leverage Round
          <PercentageBarWrap>
            <PercentageValue>51%</PercentageValue>
            <PercentageBar />
          </PercentageBarWrap>
        </Title> */}
        <Title>Leverage Round</Title>
        <Row>
          <Label>
            Available Leverage
            <Tooltip position="right" name="AvailableLeverage">
              <p>
                During a Leverage Round, a set amount of leverage (lvUSD) is made available until it is all acquired.{' '}
                <a
                  href="https://docs.archimedesfi.com/taking-leverage-(borrower)/leverage-rounds"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>
              </p>
            </Tooltip>
          </Label>
          <Value>
            {leverageAvailable ? (
              <>
                <CountUp end={Number(leverageAvailable)} decimals={2} /> lvUSD
              </>
            ) : (
              '--'
            )}
          </Value>
        </Row>
        <Row>
          <Label>
            Current Leverage Price
            <Tooltip position="right" name="CurrentLeveragePrice">
              <p>
                The cost of Leverage (lvUSD) is priced and paid for in ARCH tokens. Here we show how much lvUSD that 1
                ARCH token will get you. During a Leverage Round, the overall cost of leverage (lvUSD) starts higher and
                drops over a period of days.
                <a
                  href="https://docs.archimedesfi.com/taking-leverage-(borrower)/leverage-rounds"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>
              </p>
            </Tooltip>
          </Label>
          <Value>
            {archToLevRatio ? (
              <>
                <CountUp end={archToLevRatio} duration={1} decimals={2} /> lvUSD
              </>
            ) : (
              '--'
            )}
          </Value>
        </Row>
        {/* <Row>
          <Label>ARCH Price</Label>
          <Value>
            {archPriceFormatted ? (
              <>
                $<CountUp end={archPriceFormatted} duration={1} decimals={2} />
              </>
            ) : (
              '--'
            )}
          </Value>
        </Row> */}
      </Container>
      <Container>
        <Title>Your Position</Title>
        <Row>
          <Label>
            Est. Net APY
            <NetApyCalculationTooltip
              ousdApy={ousdAPY}
              leverageMultiplier={leverageMultiplier + 1}
              leveragedApy={leveragedApy}
              multiplierBasedOnPerformanceFee={multiplierBasedOnPerformanceFee}
              finalOusdApy={finalOusdApy}
            />
          </Label>
          <Highlighted>
            {finalOusdApy ? (
              <>
                <CountUp end={finalOusdApy} duration={1} decimals={2} /> %
              </>
            ) : (
              '--'
            )}
          </Highlighted>
        </Row>
        <Row>
          <Label>Collateral</Label>
          <Value>
            {collateral ? (
              <>
                <CountUp end={collateral} duration={1} decimals={2} /> OUSD
              </>
            ) : (
              '--'
            )}
          </Value>
          {/* <Value>{collateral ? `${collateral} OUSD`  : '--'}</Value> */}
        </Row>
        <Row>
          <Label>Leverage</Label>
          <Value>
            {leverageMultiplierFormatted ? (
              <>
                x<CountUp end={leverageMultiplierFormatted} duration={1} decimals={2} />
              </>
            ) : (
              '--'
            )}
          </Value>
        </Row>
        <Row>
          <Label>Est. Borrowed</Label>
          <Value>
            {estimatedPosition ? (
              <>
                <CountUp end={estimatedPosition} duration={1} decimals={2} /> OUSD
              </>
            ) : (
              '--'
            )}
          </Value>
        </Row>
        <Row>
          <Label>
            Position Lifetime (Close Anytime)
            <Tooltip position="right" name="PositionLifetime">
              <p>
                Positions automatically expire/unwind {positionLifetimeInDays} days after the open date. Your funds
                (collateral + earnings) can then be claimed any time on the &apos;My Dashboard&apos; page.
              </p>
            </Tooltip>
          </Label>
          <Value>
            {positionLifetimeInDays ? (
              <>
                <CountUp end={positionLifetimeInDays} duration={1} /> Days
              </>
            ) : (
              '--'
            )}
          </Value>
        </Row>
      </Container>
      <Container>
        <Title>Fees</Title>
        <Row>
          <Label>
            Origination Fee
            <Tooltip position="right" name="OriginationFee">
              <p>
                This fee is applied for opening a position and is a % of your borrowed amount. It will be deducted from
                your collateral.
              </p>
            </Tooltip>
          </Label>
          <Value>{originationFeeContent}</Value>
        </Row>
        <Row>
          <Label>
            ARCH Token Fee
            <Tooltip position="right" name="ArchTokenFee">
              <p>
                This is the upfront fee required to pay for your position&apos;s leverage. The amount will be paid in
                ARCH tokens
              </p>
            </Tooltip>
          </Label>
          <Value>{archTokenContent}</Value>
        </Row>
      </Container>
    </Wrapper>
  );
};
